/**
 * Rate limit checking logic
 */

import type { RateLimitEntry } from '../types.js';
import { getCurrentTime } from './timeUtils.js';

export function cleanQueries(priorQueries: RateLimitEntry[], interval: number): RateLimitEntry[] {
  const timeIntervalCutoff = getCurrentTime() - interval;
  return priorQueries.filter(query => query.time >= timeIntervalCutoff);
}

export function isRequestPerSingleRpc(
  priorQueries: RateLimitEntry[],
  method: string,
  maxRequests: number,
  interval: number
): number | false {
  const timeIntervalCutoff = getCurrentTime() - interval;
  
  const qualifyingRequests = priorQueries
    .filter(query => query.method === method && query.time >= timeIntervalCutoff)
    .map(query => query.time);
  
  const lenQualifying = qualifyingRequests.length;
  
  if (lenQualifying >= maxRequests) {
    console.log(`request_per_single_rpc ${lenQualifying} of ${maxRequests} hit within ${interval} seconds`);
    return qualifyingRequests[lenQualifying - maxRequests] - timeIntervalCutoff;
  }
  
  return false;
}

export function isRequestPerSingleIp(
  priorQueries: RateLimitEntry[],
  maxRequests: number,
  interval: number
): number | false {
  const timeIntervalCutoff = getCurrentTime() - interval;
  
  const qualifyingRequests = priorQueries
    .filter(query => query.time >= timeIntervalCutoff)
    .map(query => query.time);
  
  const lenQualifying = qualifyingRequests.length;
  
  if (lenQualifying >= maxRequests) {
    console.log(`request_per_single_ip ${lenQualifying} of limit ${maxRequests} hit within ${interval} seconds`);
    return qualifyingRequests[lenQualifying - maxRequests] - timeIntervalCutoff;
  }
  
  return false;
}

export function isDataPerIp(
  priorQueries: RateLimitEntry[],
  maxData: number,
  interval: number,
  avgData: number
): number | false {
  const timeIntervalCutoff = getCurrentTime() - interval;
  
  const relevantQueries = priorQueries.filter(query => query.time >= timeIntervalCutoff);
  let totalData = relevantQueries.reduce((sum, query) => sum + (query.data || 0), 0) + avgData;
  
  if (totalData >= maxData) {
    let targetTime = priorQueries[0]?.time || getCurrentTime();
    console.log(`data_per_ip ${totalData} of limit ${maxData} hit within ${interval} seconds`);
    
    if (avgData) {
      const targetData = maxData - avgData;
      for (const data of priorQueries) {
        totalData -= data.data || 0;
        if (totalData <= targetData) {
          targetTime = data.time;
          break;
        }
      }
    }
    
    return targetTime - timeIntervalCutoff;
  }
  
  return false;
}

export function getIsLimit(
  priorQueries: RateLimitEntry[],
  method: string = 'default_method',
  rateLimit: number = 40,
  rpsLimit: number = 100,
  interval: number = 10,
  dataInterval: number = 30,
  dataLimit: number = 100,
  avgData: number | null = null
): number | false {
  const requestPerSingleRpc = isRequestPerSingleRpc(priorQueries, method, rateLimit, interval);
  if (requestPerSingleRpc !== false) {
    return requestPerSingleRpc;
  }
  
  const requestPerSingleIp = isRequestPerSingleIp(priorQueries, rpsLimit, interval);
  if (requestPerSingleIp !== false) {
    return requestPerSingleIp;
  }
  
  const dataPerSingleIp = isDataPerIp(priorQueries, dataLimit, dataInterval, avgData || 0);
  if (dataPerSingleIp !== false) {
    return dataPerSingleIp;
  }
  
  return false;
}
